import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme.js';
import { getDensityPreset, normalizeDensity, UI_DENSITY, UI_DENSITY_ORDER } from '../theme/density.js';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    // Check localStorage for saved preference
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  const [density, setDensity] = useState(() => {
    const savedDensity = localStorage.getItem('uiDensity');
    if (savedDensity) return normalizeDensity(savedDensity);

    const savedCompact = localStorage.getItem('compactMode');
    if (savedCompact != null) {
      return savedCompact === 'true' ? UI_DENSITY.COMPACT : UI_DENSITY.COMFORTABLE;
    }

    const appMode = localStorage.getItem('appMode');
    const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

    if (isCoarsePointer) return UI_DENSITY.COMFORTABLE;
    if (appMode === 'hospital') return UI_DENSITY.EMR;
    return UI_DENSITY.COMPACT;
  });

  // Create theme based on mode and compact settings
  const theme = useMemo(() => {
    return createAppTheme(mode, density);
  }, [mode, density]);

  const densityPreset = useMemo(() => getDensityPreset(density), [density]);
  const compactMode = density !== UI_DENSITY.COMFORTABLE;

  const toggleColorMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const updateDensity = (newDensity) => {
    const next = normalizeDensity(newDensity);
    setDensity(next);
    localStorage.setItem('uiDensity', next);
    localStorage.setItem('compactMode', String(next !== UI_DENSITY.COMFORTABLE));
  };

  const cycleDensity = () => {
    const idx = UI_DENSITY_ORDER.indexOf(density);
    const next = UI_DENSITY_ORDER[(idx + 1) % UI_DENSITY_ORDER.length];
    updateDensity(next);
  };

  const toggleCompactMode = () => {
    updateDensity(compactMode ? UI_DENSITY.COMFORTABLE : UI_DENSITY.COMPACT);
  };

  const value = {
    mode,
    compactMode,
    density,
    densityPreset,
    setDensity: updateDensity,
    cycleDensity,
    isDark: mode === 'dark',
    toggleTheme: toggleColorMode,
    toggleColorMode,
    toggleCompactMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
